import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import CachedIcon from '@mui/icons-material/Cached';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { PreAdmissionTab } from '@/api/enum/common.enum';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	useGetPreAdmissionDetailQuery,
	useUpdatePreAdmissionStatusMutation,
} from '@/api/pre-admission/preAdmission';
import AppAgeInput from '@/common/components/form-inputs/AppAgeInput';
import {
	ageSchemaFields,
	toAgeFormValues,
	toAgePayload,
} from '@/utils/ageHelper';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useGetDicListQuery, useGetListByParamsQuery } from '@/api/common';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { RoleType } from '@/api/enum/userRole.enum';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useMemberDuplicationCheck } from '@/common/components/member-duplication/useMemberDuplicationCheck';
import PendingClosedMemberAlert from '@/common/components/member-duplication/PendingClosedMemberAlert';
import { ClosedMemberDuplicationCheckDTO } from '@/api/DTO/member-management/memberDuplication';

interface Props {
	/** Already linked to a service-closed member (system or Excel): skip that step of the check. */
	alreadyLinked?: boolean;
}

export default function SetStatusDialog({ alreadyLinked }: Props) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [updatePreAdmissionStatus] = useUpdatePreAdmissionStatusMutation();
	const { runDuplicationCheck, duplicationDialogs } =
		useMemberDuplicationCheck();
	// Result of the duplication check in this dialog session: `undefined` = not
	// checked yet; otherwise the picked closed member (or `null` for none).
	const [checkedClosedMember, setCheckedClosedMember] = useState<
		ClosedMemberDuplicationCheckDTO | null | undefined
	>(undefined);
	const { preAdmissionId } = useParams();
	const userInfo = useSelector(selectMyInfo);

	const { data: preAdmissionDetails } = useGetPreAdmissionDetailQuery(
		preAdmissionId || '',
		{ skip: !preAdmissionId }
	);

	const { data: notSuitableReasonDicList } = useGetDicListQuery(
		'not_suitable_reason',
		{ refetchOnMountOrArgChange: true }
	);

	const { data: details }: any = useGetListByParamsQuery(
		{
			branchId: userInfo?.currentBranch.id,
			roleType: RoleType.OIC_AOIC_CL,
		},
		{ refetchOnMountOrArgChange: true, skip: !userInfo?.currentBranch }
	);

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		preAdmissionStatus: Yup.string().required(t('errorMessage.pleaseSelect')),
		caseSupervisorId: Yup.string().when('preAdmissionStatus', {
			is: (preAdmissionStatus: PreAdmissionTab) =>
				preAdmissionStatus == PreAdmissionTab.submitted,
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		notSuitableReason: Yup.string().when('preAdmissionStatus', {
			is: (preAdmissionStatus: PreAdmissionTab) =>
				preAdmissionStatus == PreAdmissionTab.notSuitable,
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		notSuitableReasonSpecify: Yup.string().when('notSuitableReason', {
			is: (a: string) => a && notSuitableReasonIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		...ageSchemaFields(t, {
			getOriginalEstimatedAge: () => preAdmissionDetails?.data?.estimatedAge,
			activeWhen: [
				'preAdmissionStatus',
				(preAdmissionStatus: PreAdmissionTab) =>
					preAdmissionStatus == PreAdmissionTab.submitted,
			],
		}),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	const watchPreAdmissionStatus = methods.watch('preAdmissionStatus');
	const watchNotSuitableReason = methods.watch('notSuitableReason');

	const notSuitableReasonIsOther = () => {
		const obj = notSuitableReasonDicList?.data.find(
			(item: any) => item.id == watchNotSuitableReason
		);
		if (obj && obj.code == '12') return true;
		return false;
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		// CHA-002 check point 2: Pre-admission → Admission Request.
		// A picked closed member is shown in this dialog first; the next Confirm
		// submits and links it in the same request.
		let closedMember = checkedClosedMember;
		if (
			data.preAdmissionStatus == PreAdmissionTab.submitted &&
			closedMember === undefined
		) {
			const check = await runDuplicationCheck(
				{ userId: preAdmissionId || '' },
				{ skipClosedCheck: !!alreadyLinked, confirmLink: false }
			);
			if (!check.proceed) return;
			closedMember = check.closedMember ?? null;
			setCheckedClosedMember(closedMember);
			if (closedMember) return;
		}
		const closedUserId =
			data.preAdmissionStatus == PreAdmissionTab.submitted
				? closedMember?.id
				: undefined;

		// The link runs in the same transaction as the status change.
		const { ageSource, dateOfBirth, estimatedAge, ...statusData } = data;
		const response = await updatePreAdmissionStatus({
			...statusData,
			...(data.preAdmissionStatus == PreAdmissionTab.submitted
				? toAgePayload({ ageSource, dateOfBirth, estimatedAge })
				: {}),
			id: preAdmissionId,
			...(closedUserId ? { closedUserId } : {}),
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updatePreAdmissionStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setOpen(false);
	};

	const preAdmissionStatusList = [
		{
			id: PreAdmissionTab.submitted,
			name: t('preAdmission.updateStatusText1'),
		},
		{
			id: PreAdmissionTab.notSuitable,
			name: t('preAdmission.updateStatusText2'),
		},
	];

	const formContent = (
		<>
			<Button
				sx={{ textTransform: 'none', marginRight: '24px' }}
				variant='contained'
				onClick={() => {
					setCheckedClosedMember(undefined);
					const age = toAgeFormValues(preAdmissionDetails?.data);
					methods.setValue('ageSource', age.ageSource);
					methods.setValue('dateOfBirth', age.dateOfBirth);
					methods.setValue('estimatedAge', age.estimatedAge);
					methods.clearErrors(['dateOfBirth', 'estimatedAge']);
					setOpen(true);
				}}
				endIcon={<CachedIcon />}
			>
				{t('button.updateStatus')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='sm'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('preAdmission.setStatusDialogSelect1')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					{watchPreAdmissionStatus == PreAdmissionTab.submitted &&
						checkedClosedMember && (
							<PendingClosedMemberAlert closedMember={checkedClosedMember} />
						)}
					<Box
						display='flex'
						justifyContent='space-between'
						alignItems='center'
						marginBottom={'10px'}
					>
						<Typography
							variant='subtitle2'
							sx={{
								display: 'flex',
							}}
						>
							{t('preAdmission.setStatusDialogText')}
						</Typography>
					</Box>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={12}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='preAdmissionStatus'
										label={t('preAdmission.setStatusDialogSelect1')}
										options={preAdmissionStatusList}
									/>
								</Grid>
								{watchPreAdmissionStatus == PreAdmissionTab.submitted && (
									<Grid size={12}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='caseSupervisorId'
											label={t('preAdmission.setStatusDialogSelect2')}
											options={details.data || []}
										/>
									</Grid>
								)}
								{watchPreAdmissionStatus == PreAdmissionTab.submitted && (
									<AppAgeInput
										control={methods.control}
										label={t('memberAge.ageInformation') + '*'}
										inputSize={12}
									/>
								)}
								{watchPreAdmissionStatus == PreAdmissionTab.notSuitable && (
									<Grid size={12}>
										<AppDropDownMenu
											control={methods.control}
											name='notSuitableReason'
											label={t('preAdmission.notSuitableReason') + '*'}
											options={(notSuitableReasonDicList?.data || []).map(
												(item: { labelEn: any }) => {
													return {
														...item,
														name: item.labelEn,
													};
												}
											)}
											handleChange={() => {
												methods.setValue('notSuitableReasonSpecify', '');
												methods.clearErrors('notSuitableReasonSpecify');
											}}
										/>
									</Grid>
								)}
								{watchPreAdmissionStatus == PreAdmissionTab.notSuitable && (
									<Grid size={12}>
										<AppTextField
											disabled={!notSuitableReasonIsOther()}
											control={methods.control}
											name='notSuitableReasonSpecify'
											label={`${t('preAdmission.notSuitableReasonSpecify')}${
												notSuitableReasonIsOther() ? '*' : ''
											}`}
										/>
									</Grid>
								)}
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>

					<Button
						sx={{ textTransform: 'none' }}
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
			{duplicationDialogs}
		</>
	);
	return <>{formContent}</>;
}
